import { useTranslation } from 'react-i18next';
import { LegalLayout, Section, Bullets, Bullet } from './LegalLayout';

/**
 * Data Processing Agreement. EU/UK/JP procurement asks for a DPA on day one;
 * the Privacy page already promises subprocessors are "bound by a written
 * data-processing agreement", so this is the artefact that backs that claim.
 * Uses inline defaultValue copy (no new locale keys → i18n parity held).
 */
export function DpaPage() {
  const { t } = useTranslation();
  return (
    <LegalLayout
      eyebrow={t('dpa.eyebrow', { defaultValue: 'Data Processing' }) as string}
      title={t('dpa.title', { defaultValue: 'Data Processing Agreement' })}
      updated="2026-05-29"
    >
      <Section id="roles" kicker="01" title={t('dpa.s1.title', { defaultValue: 'Roles of the parties' }) as string}>
        <p>
          {t('dpa.s1.body', {
            defaultValue:
              'This Data Processing Agreement ("DPA") forms part of the Knowlez Terms of Service between Knowlez Inc. ("Processor") and the Customer ("Controller"). For workspace content the Customer indexes into Knowlez, the Customer is the data controller and Knowlez is the data processor, acting only on the Customer\'s documented instructions. Where Knowlez determines its own purposes (e.g. account administration, billing), it acts as an independent controller as described in the Privacy Policy.',
          })}
        </p>
      </Section>

      <Section id="scope" kicker="02" title={t('dpa.s2.title', { defaultValue: 'Subject matter & duration' }) as string}>
        <p>
          {t('dpa.s2.body', {
            defaultValue:
              'Knowlez processes personal data contained in documents, messages, and metadata the Customer uploads or connects, for the purpose of ingestion, indexing, hybrid retrieval, AI-assisted answers with citations, and the governance features (audit log, approvals, signals) the Customer enables. Processing lasts for the term of the subscription plus the retention window the Customer configures (default 2 years; up to 7 years on Enterprise for J-SOX evidence).',
          })}
        </p>
      </Section>

      <Section id="instructions" kicker="03" title={t('dpa.s3.title', { defaultValue: 'Processor obligations' }) as string}>
        <Bullets>
          <Bullet>Process personal data only on the Controller's documented instructions, including for international transfers, unless required otherwise by law (in which case Knowlez notifies the Controller first, where legally permitted).</Bullet>
          <Bullet>Ensure personnel authorised to process personal data are bound by confidentiality.</Bullet>
          <Bullet>Never sell personal data, never share it with advertisers, and never use Customer workspace content to train AI models — AI calls run through the model gateway under paid-API terms that exclude training.</Bullet>
          <Bullet>Implement the technical and organisational measures in §6, and assist the Controller in meeting its own security and breach-notification duties.</Bullet>
        </Bullets>
      </Section>

      <Section id="subjects" kicker="04" title={t('dpa.s4.title', { defaultValue: 'Data subject rights' }) as string}>
        <p>
          {t('dpa.s4.body', {
            defaultValue:
              'Knowlez provides self-service controls so the Controller can fulfil access, correction, deletion, export, and restriction requests directly: document deletion propagates to Postgres, Qdrant, the lexical index, and the blob store within 24 hours; per-user data export and account deletion are available from the Settings and Profile pages. Where a data subject contacts Knowlez directly, Knowlez forwards the request to the Controller and does not respond on the Controller\'s behalf except on instruction.',
          })}
        </p>
      </Section>

      <Section id="subprocessors" kicker="05" title={t('dpa.s5.title', { defaultValue: 'Subprocessors' }) as string}>
        <p>{t('dpa.s5.intro', { defaultValue: 'The Controller authorises the following subprocessors. Each is bound by a written agreement imposing data-protection obligations no less protective than this DPA:' })}</p>
        <Bullets>
          <Bullet><strong>Hetzner Online GmbH</strong> — hosting (Germany, Finland; Tokyo region for Global tier).</Bullet>
          <Bullet><strong>AI model gateway</strong> — routing AI calls across supported providers under no-training paid-API terms.</Bullet>
          <Bullet><strong>Cloudflare Inc.</strong> — DNS, CDN, geo-detection, DDoS protection.</Bullet>
          <Bullet><strong>Stripe Inc.</strong> — billing for paid plans (no workspace content shared).</Bullet>
          <Bullet><strong>Amazon SES / Postmark</strong> — transactional email.</Bullet>
        </Bullets>
        <p className="mt-3">
          {t('dpa.s5.notice', {
            defaultValue:
              'Knowlez gives at least 30 days\' notice of any new or replacement subprocessor; the Controller may object on reasonable data-protection grounds. On-prem deployments have zero subprocessors — the entire stack runs inside the Customer\'s VPC or air-gap.',
          })}
        </p>
      </Section>

      <Section id="security" kicker="06" title={t('dpa.s6.title', { defaultValue: 'Technical & organisational measures' }) as string}>
        <Bullets>
          <Bullet>TLS 1.2+ in transit; AES-256-GCM at rest for OAuth tokens, SSO secrets, and SCIM bearer tokens; bcrypt (12 rounds) for passwords.</Bullet>
          <Bullet>Tenant isolation by workspace scoping on every query, plus source-system ACLs re-checked at retrieval time so a chunk never surfaces to someone who cannot see the source.</Bullet>
          <Bullet>Append-only audit log on every mutating action; per-tenant AI rate limiting; prompt-injection fencing of untrusted retrieved content.</Bullet>
          <Bullet>Least-privilege access for personnel; encrypted backups; documented deploy with pre-migration backup and health-checked rollback.</Bullet>
        </Bullets>
      </Section>

      <Section id="transfers" kicker="07" title={t('dpa.s7.title', { defaultValue: 'International transfers' }) as string}>
        <p>
          {t('dpa.s7.body', {
            defaultValue:
              'Cloud hosting defaults to the EU (Germany/Finland), with a Tokyo region available on the Global and Enterprise tiers for APPI data-residency. Where personal data is transferred outside the EEA/UK, the transfer relies on the EU Standard Contractual Clauses (and the UK Addendum) incorporated by reference into this DPA, together with the supplementary measures in §6. Customers requiring strict data residency can elect a single-region or on-prem deployment.',
          })}
        </p>
      </Section>

      <Section id="breach" kicker="08" title={t('dpa.s8.title', { defaultValue: 'Personal data breach' }) as string}>
        <p>
          {t('dpa.s8.body', {
            defaultValue:
              'Knowlez notifies the Controller without undue delay — and in any event within 72 hours — after becoming aware of a personal data breach affecting the Controller\'s data, with the information the Controller reasonably needs to meet its own notification obligations.',
          })}
        </p>
      </Section>

      <Section id="audit" kicker="09" title={t('dpa.s9.title', { defaultValue: 'Audit & assistance' }) as string}>
        <p>
          {t('dpa.s9.body', {
            defaultValue:
              'Knowlez makes available the information necessary to demonstrate compliance with this DPA and, on reasonable notice and subject to confidentiality, allows for audits. Where available, an independent third-party report (e.g. a SOC 2 report once issued) satisfies audit requests. Knowlez\'s compliance posture and current certification status are published on the Security page.',
          })}
        </p>
      </Section>

      <Section id="termination" kicker="10" title={t('dpa.s10.title', { defaultValue: 'Return & deletion on termination' }) as string}>
        <p>
          {t('dpa.s10.body', {
            defaultValue:
              'On termination, and at the Controller\'s choice, Knowlez returns or deletes all personal data processed on the Controller\'s behalf (including from indexes and backups, subject to backup-rotation windows), unless retention is required by law. Account data is deleted within 30 days of account closure. Requests: privacy@knowlez.com.',
          })}
        </p>
      </Section>
    </LegalLayout>
  );
}
